import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccountTree as AccountTreeIcon,
  Info as InfoIcon,
  Percent as PercentIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../hooks/useAuth';
import { formatCommissionAmount } from '../utils/formatters';
import './DownlineManagement.css';

interface DownlineInfo {
  id: number;
  username: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  commissionSharePercentage: number;
  createdAt: string;
  lastLoginAt: string;
  totalDownlines: number;
  totalCommissionsGenerated: number;
  operatorCommissions: OperatorCommission[];
  salesPercentages: OperatorSalesPercentage[];
}

interface OperatorSalesPercentage {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  allocatedPercentage: number; // What upline allocated
  usedPercentage: number; // What downline is actually using
}

interface OperatorCommission {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  commissionPercentage: number;
}

interface CreateDownlineRequest {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  password: string;
  operatorSalesPercentages: OperatorSalesPercentageInput[];
}

interface OperatorSalesPercentageInput {
  operatorId: number;
  salesPercentage: number;
}

interface CreateDownlineResponse {
  userId: number;
  username: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  commissionSharePercentage: number;
  operatorCommissions: OperatorCommission[];
}

interface UpdateDownlineSalesPercentageRequest {
  DownlineId: number;
  OperatorId: number;
  SalesPercentage: number;
}

interface DownlineStats {
  totalDownlines: number;
  activeDownlines: number;
  totalCommissionEarned: number;
  totalCommissionPaidOut: number;
  operatorBreakdown: {
    operatorId: number;
    operatorCode: string;
    operatorName: string;
    totalCommissionGenerated: number;
    activeDownlines: number;
  }[];
}

const DownlineManagement: React.FC = () => {
  console.log('🔍 DownlineManagement component mounted');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDownline, setSelectedDownline] = useState<DownlineInfo | null>(null);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [editingSales, setEditingSales] = useState<Record<number, string | number>>({});
  const [editingOperatorSales, setEditingOperatorSales] = useState<Record<number, string>>({});
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<CreateDownlineRequest>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    operatorSalesPercentages: []
  });


  // Fetch downlines
  const { data: downlines = [], isLoading: downlinesLoading, refetch: refetchDownlines } = useQuery<DownlineInfo[]>({
    queryKey: ['my-downlines'],
    queryFn: async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/user/my-downlines`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch downlines');
      return response.json();
    }
  });

  // Function to refresh downline data
  const refreshDownlineData = async () => {
    try {
      // Invalidate and refetch both queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-downlines'] }),
        queryClient.invalidateQueries({ queryKey: ['operators'] }),
        queryClient.invalidateQueries({ queryKey: ['downline-stats'] })
      ]);
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error refreshing data:', error);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    }
  };

  // Get operators from the first downline's operatorCommissions (they should all have the same operators)
  // Fetch operators data from API
  const { data: operatorsData = [], error: operatorsError, isLoading: operatorsLoading } = useQuery<OperatorCommission[]>({
    queryKey: ['operators'],
    enabled: true, // Explicitly enable the query
    retry: 1, // Only retry once on failure
    refetchOnMount: true, // Override global setting
    queryFn: async () => {
      console.log('🔍 Fetching operators from API...');
      const token = localStorage.getItem('token');
      console.log('🔍 Token from localStorage:', token ? `Present (${token.substring(0, 20)}...)` : 'Not found');
      console.log('🔍 Full token length:', token ? token.length : 0);
      console.log('🔍 Token dots count:', token ? (token.match(/\./g) || []).length : 0);
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      const authHeader = `Bearer ${token}`;
      console.log('🔍 Authorization header:', authHeader.substring(0, 50) + '...');
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/operator`, {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      });
      console.log('🔍 Operators API response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Operators API error:', errorText);
        
        if (response.status === 401) {
          // Token is invalid, clear it and redirect to login
          localStorage.removeItem('token');
          throw new Error('Authentication failed. Please log in again.');
        }
        
        throw new Error(`Failed to fetch operators: ${response.status} ${errorText}`);
      }
      const operators = await response.json();
      console.log('🔍 Raw operators data:', operators);
      // Transform the response to match OperatorCommission interface
      const transformed = operators.map((op: any) => ({
        operatorId: op.id,
        operatorCode: op.code,
        operatorName: op.name,
        commissionPercentage: 0 // Default commission percentage
      }));
      console.log('🔍 Transformed operators data:', transformed);
      return transformed;
    }
  });

  // Debug operators query state
  console.log('🔍 Operators query state:', {
    isLoading: operatorsLoading,
    error: operatorsError,
    dataLength: operatorsData.length,
    data: operatorsData
  });

  // Test if query is actually running
  useEffect(() => {
    console.log('🔍 useEffect triggered - operators query should run now');
  }, []);

  // Fetch commission management data to get remaining commission for validation
  const { data: commissionData, error: commissionError, isLoading: commissionLoading } = useQuery({
    queryKey: ['commission-management'],
    queryFn: async () => {
      console.log('🔍 Fetching commission management data...');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/user/commission-management`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('🔍 Commission management API response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Commission management API error:', errorText);
        throw new Error(`Failed to fetch commission management: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      console.log('🔍 Raw commission management data:', data);
      return data;
    }
  });

  // Debug commission data
  console.log('🔍 Commission data state:', {
    commissionData,
    commissionError,
    commissionLoading,
    operatorCommissions: commissionData?.operatorCommissions
  });

  console.log('🔍 Operators data:', operatorsData);
  console.log('🔍 Operators error:', operatorsError);
  console.log('🔍 Operators loading:', operatorsLoading);

  // Fetch downline stats
  const { data: stats } = useQuery<DownlineStats>({
    queryKey: ['downline-stats'],
    queryFn: async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/user/downline-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch downline stats');
      return response.json();
    }
  });

  // Create downline mutation
  const createDownlineMutation = useMutation<CreateDownlineResponse, Error, CreateDownlineRequest>({
    mutationFn: async (data) => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/user/create-downline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create downline');
      }
      
      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Downline created successfully!', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['my-downlines'] });
      queryClient.invalidateQueries({ queryKey: ['downline-stats'] });
      handleCloseCreateDialog();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  // Update commission share mutation
  const updateShareMutation = useMutation<void, Error, { downlineId: number; commissionSharePercentage: number }>({
    mutationFn: async ({ downlineId, commissionSharePercentage }) => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/user/update-downline-share`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ downlineId, commissionSharePercentage })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update commission share');
      }
    },
    onSuccess: () => {
      enqueueSnackbar('Commission share updated successfully!', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['my-downlines'] });
      handleCloseEditDialog();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const handleOpenCreateDialog = () => {
    console.log('🔍 Opening create dialog, operatorsData:', operatorsData);
    
    // Initialize operator sales amounts based on user role
    const defaultAmount = user?.role === 'admin' ? 0.6 : 0; // RM 0.60 for admin, RM 0 for downlines
    const operatorSalesPercentages = operatorsData.map(op => ({
      operatorId: op.operatorId,
      salesPercentage: defaultAmount
    }));

    console.log('🔍 Initialized operatorSalesPercentages:', operatorSalesPercentages);
    console.log('🔍 User role:', user?.role, 'Default amount:', defaultAmount);

    // Initialize editing state with string values for free typing
    const initialEditingSales: Record<number, string> = {};
    operatorsData.forEach(op => {
      initialEditingSales[op.operatorId] = defaultAmount.toFixed(2);
    });

    setEditingOperatorSales(initialEditingSales);
    setFormData({
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      phoneNumber: '',
      password: '',
      operatorSalesPercentages
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setEditingOperatorSales({});
  };

  const handleOpenEditDialog = (downline: DownlineInfo) => {
    setSelectedDownline(downline);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedDownline(null);
  };

  const handleCreateDownline = () => {
    // First, sync editingOperatorSales to formData before validation
    const operatorSalesPercentages = operatorsData.map(op => {
      const editingValue = editingOperatorSales[op.operatorId];
      const numericValue = editingValue ? parseFloat(editingValue) : (user?.role === 'admin' ? 0.6 : 0);
      return {
        operatorId: op.operatorId,
        salesPercentage: isNaN(numericValue) ? 0 : numericValue
      };
    });

    // Update formData with parsed values
    const updatedFormData = {
      ...formData,
      operatorSalesPercentages
    };

    // Validation
    const errors: string[] = [];
    if (!formData.firstName.trim()) errors.push('First name is required');
    if (!formData.lastName.trim()) errors.push('Last name is required');
    if (!formData.username.trim()) errors.push('Username is required');
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.phoneNumber.trim()) errors.push('Phone number is required');
    if (!formData.password || formData.password.length < 6) errors.push('Password must be at least 6 characters');
    
    // Validate operator sales amounts
    operatorSalesPercentages.forEach((op, index) => {
      if (op.salesPercentage < 0) {
        errors.push(`Sales amount for operator ${index + 1} must be RM 0 or greater`);
      } else if (op.salesPercentage > 0.6) {
        // Always allow up to 0.6 when creating downlines, regardless of user's own MaxAllowed
        const operatorName = operatorsData.find(o => o.operatorId === op.operatorId)?.operatorName || `Operator ${index + 1}`;
        errors.push(`Cannot allocate ${formatCommissionAmount(op.salesPercentage)} to ${operatorName}. Maximum is ${formatCommissionAmount(0.6)} per set.`);
      }
    });

    if (errors.length > 0) {
      enqueueSnackbar(errors.join(', '), { variant: 'error' });
      return;
    }

    createDownlineMutation.mutate(updatedFormData);
  };

  const handleUpdateCommissionShare = (newPercentage: number) => {
    if (selectedDownline) {
      const percentageDecimal = newPercentage / 100;
      updateShareMutation.mutate({
        downlineId: selectedDownline.id,
        commissionSharePercentage: percentageDecimal
      });
    }
  };

  const handleOperatorSalesPercentageChange = (operatorId: number, amount: number) => {
    setFormData(prev => {
      const existingIndex = prev.operatorSalesPercentages.findIndex(op => op.operatorId === operatorId);
      
      if (existingIndex >= 0) {
        // Update existing entry - store amount directly (no division needed)
        const updated = [...prev.operatorSalesPercentages];
        updated[existingIndex] = { ...updated[existingIndex], salesPercentage: amount };
        return {
          ...prev,
          operatorSalesPercentages: updated
        };
      } else {
        // Add new entry - store amount directly (no division needed)
        return {
          ...prev,
          operatorSalesPercentages: [
            ...prev.operatorSalesPercentages,
            { operatorId, salesPercentage: amount }
          ]
        };
      }
    });
  };

  // Sales percentage management
  const updateSalesMutation = useMutation({
    mutationFn: async (request: UpdateDownlineSalesPercentageRequest) => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/user/update-downline-sales`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update sales percentage');
      }

      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Sales percentage updated successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['my-downlines'] });
      handleCloseSalesDialog();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const handleOpenSalesDialog = (downline: DownlineInfo) => {
    setSelectedDownline(downline);
    const initialSales: Record<number, number> = {};
    
    // Initialize with existing sales amounts if available (no auto-conversion)
    downline.salesPercentages?.forEach(sp => {
      initialSales[sp.operatorId] = sp.allocatedPercentage;
    });
    
    // For operators without sales amounts, set default based on user role
    const defaultAmount = user?.role === 'admin' ? 0.60 : 0; // RM 0.60 for admin, RM 0 for downlines
    operatorsData.forEach(op => {
      if (!(op.operatorId in initialSales)) {
        initialSales[op.operatorId] = defaultAmount;
      }
    });
    
    console.log('🔍 Opening sales dialog for:', downline.fullName);
    console.log('🔍 Initial sales amounts:', initialSales);
    console.log('🔍 Available operators:', operatorsData);
    
    setEditingSales(initialSales);
    setSalesDialogOpen(true);
  };

  const handleCloseSalesDialog = () => {
    setSalesDialogOpen(false);
    setSelectedDownline(null);
    setEditingSales({});
  };

  const handleSaveSalesPercentages = () => {
    if (!selectedDownline) return;

    // Update each operator's sales amount (no division needed - already in RM)
    Object.entries(editingSales).forEach(([operatorId, amount]) => {
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      updateSalesMutation.mutate({
        DownlineId: selectedDownline.id, // Match DTO property name exactly
        OperatorId: parseInt(operatorId), // Match DTO property name exactly
        SalesPercentage: numericAmount || 0 // Send amount directly (e.g., 0.60 for RM 0.60/set)
      });
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getMaxAllowedPercentage = (operatorId: number) => {
    // When creating a new downline, you can always allocate up to the full base amount (0.6)
    // Your own MaxAllowedPercentage limit doesn't restrict what you can give to downlines
    // It only limits what you can take for yourself from your own sales
    return 0.6;
  };

  const handleToggleCard = (downlineId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(downlineId)) {
        newSet.delete(downlineId);
      } else {
        newSet.add(downlineId);
      }
      return newSet;
    });
  };

  return (
    <Container maxWidth={false} className="downline-management" sx={{ px: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <AccountTreeIcon className="title-icon" />
          Downline Management
        </Typography>
        <Typography variant="body1" className="page-description">
          Manage your downline network, create new agents, and configure commission sharing
        </Typography>
      </Box>

      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={3} className="stats-grid">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card className="stat-card">
              <CardContent>
                <Box className="stat-content">
                  <PeopleIcon className="stat-icon" />
                  <Box className="stat-text">
                    <Typography variant="h4">{stats.totalDownlines}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Downlines
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card className="stat-card">
              <CardContent>
                <Box className="stat-content">
                  <TrendingUpIcon className="stat-icon" />
                  <Box className="stat-text">
                    <Typography variant="h4">{stats.activeDownlines}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Downlines
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card className="stat-card">
              <CardContent>
                <Box className="stat-content">
                  <Box className="stat-icon">💰</Box>
                  <Box className="stat-text">
                    <Typography variant="h4">{formatCurrency(stats.totalCommissionEarned)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Earned
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card className="stat-card">
              <CardContent>
                <Box className="stat-content">
                  <Box className="stat-icon">💸</Box>
                  <Box className="stat-text">
                    <Typography variant="h4">{formatCurrency(stats.totalCommissionPaidOut)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Paid Out
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content */}
      <Card className="main-content-card">
        <CardContent>
          <Tabs 
            value={tabValue} 
            onChange={(_, newValue) => setTabValue(newValue)}
            className="content-tabs"
          >
            <Tab label="My Downlines" icon={<PeopleIcon />} />
            <Tab label="Statistics" icon={<TrendingUpIcon />} />
          </Tabs>

          {/* Tab Panel 1: My Downlines */}
          <Box className="tab-panel" style={{ display: tabValue === 0 ? 'block' : 'none' }}>
            <Box className="panel-header">
              <Typography variant="h5" component="h2">
                My Downlines ({downlines.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={refreshDownlineData}
                  className="refresh-button"
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateDialog}
                  className="create-button"
                >
                  Create New Downline
                </Button>
              </Box>
            </Box>

            {downlinesLoading ? (
              <Box className="loading-container">
                <CircularProgress />
                <Typography variant="body1" className="loading-text">
                  Loading downlines...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Desktop Table View */}
                {!isMobile && (
                  <TableContainer component={Paper} className="downlines-table" sx={{ overflowX: 'auto', width: '100%' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Agent Info</TableCell>
                          <TableCell>Commission Share (RM/set)</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                          {operatorsData.map((operator) => (
                            <TableCell key={operator.operatorId} align="center" sx={{ minWidth: 80 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {operator.operatorName}
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {downlines.map((downline) => (
                          <TableRow key={downline.id}>
                            <TableCell>
                              <Box className="agent-info">
                                <Typography variant="subtitle2" className="agent-name">
                                  {downline.fullName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" className="agent-username">
                                  @{downline.username}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={formatCommissionAmount(downline.commissionSharePercentage)}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={downline.lastLoginAt ? 'Active' : 'Inactive'}
                                color={downline.lastLoginAt ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDate(downline.createdAt)}
                              </Typography>
                            </TableCell>
                            {operatorsData.map((operator) => {
                              const salesPercentage = downline.salesPercentages?.find(sp => sp.operatorId === operator.operatorId);
                              
                              // Display exactly what backend provides (amounts in RM)
                              // Do not auto-convert small values so user can input values like 0.05 intentionally
                              const maxAllowedAmount = salesPercentage ? salesPercentage.allocatedPercentage : 0;
                              const actualUsedAmount = salesPercentage ? salesPercentage.usedPercentage : 0;
                              
                              return (
                                <TableCell key={operator.operatorId} align="center">
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      label={`Max: ${formatCommissionAmount(maxAllowedAmount)}`}
                                      size="small"
                                      color={maxAllowedAmount > 0 ? 'primary' : 'default'}
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                      Used: {formatCommissionAmount(actualUsedAmount)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Tooltip title="Edit Sales Amounts">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenSalesDialog(downline)}
                                  color="primary"
                                >
                                  <PercentIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Mobile Card View */}
                {isMobile && (
                  <Box className="mobile-downlines-container">
                    {downlines.map((downline) => {
                      const isExpanded = expandedCards.has(downline.id);
                      return (
                        <Card key={downline.id} className="mobile-downline-card">
                          <CardContent sx={{ p: 0 }}>
                            {/* Header Section - Always Visible */}
                            <Box 
                              className="mobile-card-header"
                              onClick={() => handleToggleCard(downline.id)}
                              sx={{ 
                                cursor: 'pointer',
                                px: 2,
                                py: 1.5,
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <Box className="mobile-agent-info" sx={{ flex: 1 }}>
                                <Typography variant="h6" className="mobile-agent-name">
                                  {downline.fullName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" className="mobile-agent-username">
                                  @{downline.username}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  label={downline.lastLoginAt ? 'Active' : 'Inactive'}
                                  color={downline.lastLoginAt ? 'success' : 'default'}
                                  size="small"
                                  className="mobile-status-chip"
                                />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleCard(downline.id);
                                  }}
                                  sx={{ 
                                    transition: 'transform 0.2s',
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Collapsible Content */}
                            <Collapse in={isExpanded}>
                              <Box sx={{ px: 2, pb: 2 }}>
                                {/* Commission Share Section */}
                                <Box className="mobile-commission-section">
                                  <Typography variant="body2" color="text.secondary" className="mobile-section-label">
                                    Commission Share
                                  </Typography>
                                  <Chip 
                                    label={formatCommissionAmount(downline.commissionSharePercentage)}
                                    color="primary"
                                    variant="outlined"
                                    size="medium"
                                    className="mobile-commission-chip"
                                  />
                                </Box>

                                {/* Operator Sales Section */}
                                {operatorsData.length > 0 && (
                                  <Box className="mobile-operators-section">
                                    <Typography variant="body2" color="text.secondary" className="mobile-section-label">
                                      Operator Sales (RM/set)
                                    </Typography>
                                    <Grid container spacing={1.5} sx={{ mt: 1 }}>
                                      {operatorsData.map((operator) => {
                                        const salesPercentage = downline.salesPercentages?.find(sp => sp.operatorId === operator.operatorId);
                                        const maxAllowedAmount = salesPercentage ? salesPercentage.allocatedPercentage : 0;
                                        const actualUsedAmount = salesPercentage ? salesPercentage.usedPercentage : 0;
                                        
                                        return (
                                          <Grid size={{ xs: 6, sm: 4 }} key={operator.operatorId}>
                                            <Box className="mobile-operator-item">
                                              <Typography variant="caption" className="mobile-operator-name" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                {operator.operatorName}
                                              </Typography>
                                              <Chip
                                                label={`Max: ${formatCommissionAmount(maxAllowedAmount)}`}
                                                size="small"
                                                color={maxAllowedAmount > 0 ? 'primary' : 'default'}
                                                variant="outlined"
                                                className="mobile-operator-chip"
                                                sx={{ fontSize: '0.7rem', mb: 0.5 }}
                                              />
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                                Used: {formatCommissionAmount(actualUsedAmount)}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Box>
                                )}

                                {/* Metadata Section */}
                                <Box className="mobile-metadata-section">
                                  <Typography variant="caption" color="text.secondary">
                                    Created: {formatDate(downline.createdAt)}
                                  </Typography>
                                </Box>

                                {/* Actions Section */}
                                <Box className="mobile-actions-section">
                                  <Button
                                    variant="contained"
                                    startIcon={<PercentIcon />}
                                    onClick={() => handleOpenSalesDialog(downline)}
                                    fullWidth
                                    className="mobile-edit-button"
                                    sx={{ mt: 2 }}
                                  >
                                    Edit Sales Amounts
                                  </Button>
                                </Box>
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Tab Panel 2: Statistics */}
          <Box className="tab-panel" style={{ display: tabValue === 1 ? 'block' : 'none' }}>
            <Typography variant="h5" component="h2" className="stats-title">
              Commission Statistics by Operator
            </Typography>
            
            {stats?.operatorBreakdown.map((operator) => (
              <Card key={operator.operatorId} className="operator-stat-card">
                <CardContent>
                  <Box className="operator-stat-content">
                    <Box className="operator-info">
                      <Typography variant="h6">{operator.operatorName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {operator.operatorCode}
                      </Typography>
                    </Box>
                    <Box className="operator-metrics">
                      <Box className="metric">
                        <Typography variant="h4" color="primary">
                          {formatCurrency(operator.totalCommissionGenerated)}
                        </Typography>
                        <Typography variant="body2">Total Generated</Typography>
                      </Box>
                      <Box className="metric">
                        <Typography variant="h4" color="secondary">
                          {operator.activeDownlines}
                        </Typography>
                        <Typography variant="body2">Active Agents</Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Create Downline Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create New Downline Agent</DialogTitle>
        <DialogContent>
          <Alert severity="info" className="create-alert">
            <InfoIcon />
            This will create a new Agent account that you can manage and configure commission sharing.
          </Alert>
          
          <Grid container spacing={2} className="create-form">
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Sales Percentages by Operator
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Set the maximum sales percentage this downline can use for each operator (required)
              </Typography>
              {downlinesLoading && (
                <Typography variant="body2" color="info.main">
                  Loading operators...
                </Typography>
              )}
            </Grid>
            {operatorsLoading ? (
              <Grid size={{ xs: 12 }}>
                <Typography>Loading operators...</Typography>
              </Grid>
            ) : operatorsError ? (
              <Grid size={{ xs: 12 }}>
                <Typography color="error">
                  Error loading operators: {operatorsError.message}
                </Typography>
                {operatorsError.message.includes('Authentication failed') && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please refresh the page and log in again.
                  </Typography>
                )}
              </Grid>
            ) : operatorsData.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Typography color="error">
                  No operators available. Please check if operators are loaded.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Operators data: {JSON.stringify(operatorsData)}
                </Typography>
              </Grid>
            ) : (
              operatorsData.map((operator) => {
                const defaultDisplayValue = user?.role === 'admin' ? '0.60' : '0.00';
                return (
                  <Grid key={operator.operatorId} size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={`${operator.operatorName} Sales (RM/set)`}
                      type="text"
                      value={editingOperatorSales[operator.operatorId] !== undefined ? editingOperatorSales[operator.operatorId] : defaultDisplayValue}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        // Allow free typing including partial values like "0.", "0.5"
                        setEditingOperatorSales(prev => ({
                          ...prev,
                          [operator.operatorId]: rawValue
                        }));
                      }}
                      onBlur={(e) => {
                        // Validate and clamp to your available amount from DB
                        const value = parseFloat(e.target.value);
                        const maxAvailable = getMaxAllowedPercentage(operator.operatorId);
                        if (!isNaN(value)) {
                          const clamped = Math.min(Math.max(value, 0), maxAvailable);
                          setEditingOperatorSales(prev => ({
                            ...prev,
                            [operator.operatorId]: clamped.toFixed(2)
                          }));
                        } else {
                          setEditingOperatorSales(prev => ({
                            ...prev,
                            [operator.operatorId]: defaultDisplayValue
                          }));
                        }
                      }}
                      helperText={`Max available: ${formatCommissionAmount(getMaxAllowedPercentage(operator.operatorId))}/set for ${operator.operatorName}`}
                      required
                      error={false}
                    />
                  </Grid>
                );
              })
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button 
            onClick={handleCreateDownline} 
            variant="contained"
            disabled={createDownlineMutation.isPending}
          >
            {createDownlineMutation.isPending ? <CircularProgress size={20} /> : 'Create Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Commission Share Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog}>
        <DialogTitle>Update Commission Share Amount</DialogTitle>
        <DialogContent>
          {selectedDownline && (
            <Box className="edit-form">
              <Typography variant="subtitle1" className="selected-agent">
                Agent: {selectedDownline.fullName} (@{selectedDownline.username})
              </Typography>
              <TextField
                fullWidth
                label="Commission Share (RM/set)"
                type="text"
                defaultValue={selectedDownline.commissionSharePercentage.toFixed(2)}
                onChange={(e) => {
                  const amount = parseFloat(e.target.value);
                  if (amount >= 0 && amount <= 0.6) {
                    handleUpdateCommissionShare(amount * 100);
                  }
                }}
                helperText="Enter new commission share amount (max RM 0.60/set)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Sales Amount Dialog */}
      <Dialog open={salesDialogOpen} onClose={handleCloseSalesDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Set Sales Amounts (RM/set) for {selectedDownline?.fullName}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" className="create-alert">
            <InfoIcon />
            <Typography variant="body2">
              Set the maximum sales amount (RM/set) this downline is allowed to use for each operator. 
              The downline will then configure their actual usage on their Sales Management page.
            </Typography>
          </Alert>
          
          {operatorsData.map((operator) => {
            const existingSales = selectedDownline?.salesPercentages?.find(sp => sp.operatorId === operator.operatorId);
            return (
              <Box key={operator.operatorId} className="sales-percentage-item" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {operator.operatorName}
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    label="Max Allowed Amount (RM/set)"
                    type="text"
                    value={editingSales[operator.operatorId] !== undefined ? editingSales[operator.operatorId] : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Allow free typing including partial values like "0.", "0.5"
                      setEditingSales(prev => ({
                        ...prev,
                        [operator.operatorId]: rawValue
                      }));
                    }}
                    onBlur={(e) => {
                      // Validate and clamp to your available amount from DB
                      const value = parseFloat(e.target.value);
                      const maxAvailable = getMaxAllowedPercentage(operator.operatorId);
                      if (!isNaN(value)) {
                        const clamped = Math.min(Math.max(value, 0), maxAvailable);
                        setEditingSales(prev => ({
                          ...prev,
                          [operator.operatorId]: clamped
                        }));
                      } else {
                        setEditingSales(prev => ({
                          ...prev,
                          [operator.operatorId]: 0
                        }));
                      }
                    }}
                    size="small"
                    sx={{ flexGrow: 1 }}
                    helperText={`Maximum you can allocate: ${formatCommissionAmount(getMaxAllowedPercentage(operator.operatorId))}/set`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Currently using: {formatCommissionAmount(existingSales ? existingSales.usedPercentage : 0)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSalesDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveSalesPercentages}
            variant="contained"
            disabled={updateSalesMutation.isPending}
          >
            {updateSalesMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DownlineManagement;
